import { v4 } from 'uuid';
import _ from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  ButtonContainer,
  Container,
  DeleteButton,
  Dropdown,
  SkipToContainer,
  SkipToOptionsContainer,
  Text,
} from './styles';

import SkipCondition from './skip-condition';

const buildAnswers = (question) => {
  const { questionOptions: array = [], questionType } = question;
  const { length } = array;

  return _.map(array, (item, index) => {
    let text = item.label;
    const isRating = questionType === 'range';

    if (isRating && index === 0) {
      text = `${text} = ${question.leftLabel}`;
    }

    if (isRating && index === length - 1) {
      text = `${text} = ${question.rightLabel}`;
    }

    return {
      key: item.id,
      value: item.id,
      text,
    };
  });
};

const Condition = (props) => {
  /** props */
  const {
    condition,
    onDelete,
    onUpdate,
    options,
  } = props;

  const { skipToOptions, questions } = options;

  const questionOptions = useMemo(() => (
    _.map(questions, (item) => ({
      key: item.id,
      value: item.id,
      text: `${item.index + 1}. ${item.label}`,
      type: item.questionType,
      answers: buildAnswers(item),
    }))
  ), [questions]);

  /** states */
  const [skipConditions, setSkipConditions] = useState([]);

  useEffect(() => {
    setSkipConditions(condition.skipConditions);
  }, [condition.skipConditions]);

  /** <value>[Array] represents new skip conditions */
  const buildCondition = (value) => {
    const newCondition = {
      ...condition,
      skipConditions: value,
    };

    onUpdate(newCondition);
  };

  const handleChangeTarget = (value) => {
    onUpdate({ ...condition, surveyPageId: value });
  };

  const handleCreateSkipCondition = () => {
    const skipCondition = {
      id: v4(),
      questionId: '',
      questionOptionId: '',
      operation: '',
    };

    buildCondition([...condition.skipConditions, skipCondition]);
  };

  const handleUpdateSkipCondition = (skipConditionId, element) => {
    const newSkipConditions = _.map(skipConditions, (skipCondition) => {
      if (skipCondition.id === skipConditionId) {
        return { ...skipCondition, ...element };
      }

      return { ...skipCondition };
    });

    buildCondition(newSkipConditions);
  };

  const handleDeleteSkipCondition = (id) => {
    const newSkipConditions = _.filter(condition.skipConditions, (item) => item.id !== id);
    buildCondition(newSkipConditions);
  };

  return (
    <Container>
      {_.map(skipConditions, (skipCondition, index) => {
        const isFirst = index === 0;

        return (
          <SkipCondition
            key={skipCondition.id}
            data={skipCondition}
            isFirst={isFirst}
            questions={questionOptions}
            onDelete={() => handleDeleteSkipCondition(skipCondition.id)}
            onCreate={handleCreateSkipCondition}
            onUpdate={(obj) => handleUpdateSkipCondition(skipCondition.id, obj)}
          />
        );
      })}

      <SkipToContainer>
        <Text>Skip to</Text>

        <SkipToOptionsContainer>
          {/* Pages dropdown */}
          <Dropdown
            scrolling
            selection
            onChange={(e, { value }) => handleChangeTarget(value)}
            options={skipToOptions}
            value={condition.surveyPageId}
          />
        </SkipToOptionsContainer>

        <ButtonContainer>
          <DeleteButton onClick={onDelete}>
            Delete
          </DeleteButton>
        </ButtonContainer>
      </SkipToContainer>
    </Container>
  );
};

Condition.propTypes = {
  options: PropTypes.shape({
    skipToOptions: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ).isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired).isRequired,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  condition: PropTypes.shape({
    id: PropTypes.string.isRequired,
    surveyPageId: PropTypes.string.isRequired,
    skipConditions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        questionId: PropTypes.string.isRequired,
        questionOptionId: PropTypes.string.isRequired,
        operation: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};

export default Condition;
